import {
   Box,
   Button,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   IconButton,
   MenuItem,
   Tab,
   Tabs,
   TextField,
} from "@mui/material";
import * as React from "react";
import {useState} from "react";
import {Control, useFieldArray} from "react-hook-form";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {MasterEditorData, MasterEditMetaField} from "../MasterDataEditor";
import {EnumMetaFieldType} from "../../../../Queries";
import {Languages} from "../../../settings/languages/Languages";
import {FormInputText} from "../../../shop/wholesale/form/FormInputText";
import {FormInputSelect} from "../../../shop/wholesale/form/FormInputSelect";

type MetaFieldEditorDialogProps = {
   open: boolean
   onClose: () => void
   control: Control<MasterEditorData & { newTag: string, newCol: string }>
   locales: string[]
}

const metaFieldTypeOptions = Object.values(EnumMetaFieldType).map(t => ({id: t, name: t}));

export const MetaFieldEditorDialog = ({open, onClose, control, locales}: MetaFieldEditorDialogProps) => {
   const [activeTab, setActiveTab] = useState(0);

   const {fields: metaFields, append: appendMetaField, remove: removeMetaField} = useFieldArray({
      control,
      name: 'metaFields'
   });

   const {
      fields: localizedMetaFields,
      append: appendLocalizedGroup,
      remove: removeLocalizedGroup
   } = useFieldArray({
      control,
      name: 'localizedMetaFields'
   });

   const usedLanguages = localizedMetaFields.map(lm => lm.language);
   const availableLanguages = locales.filter(l => !usedLanguages.includes(l));

   const handleAddLanguage = (language: string) => {
      appendLocalizedGroup({
         language,
         metaFields: []
      });
   };

   return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
         <DialogTitle>Edit Meta Fields</DialogTitle>
         <DialogContent>
            <Box sx={{borderBottom: 1, borderColor: 'divider', marginBottom: '16px'}}>
               <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)}>
                  <Tab label="Default"/>
                  {localizedMetaFields.map((lm) => {
                     const lang = Languages.get(lm.language);
                     return <Tab key={lm.language} label={lang?.name || lm.language}/>;
                  })}
               </Tabs>
            </Box>

            {activeTab === 0 && (
               <MetaFieldList
                  fields={metaFields}
                  baseName="metaFields"
                  onAdd={() => appendMetaField(emptyMetaField())}
                  onRemove={removeMetaField}
               />
            )}

            {localizedMetaFields.map((lm, groupIndex) => (
               activeTab === groupIndex + 1 && (
                  <LocalizedMetaFieldGroup
                     key={lm.id}
                     groupIndex={groupIndex}
                     control={control}
                     onRemoveGroup={() => {
                        removeLocalizedGroup(groupIndex);
                        setActiveTab(0);
                     }}
                  />
               )
            ))}

            {availableLanguages.length > 0 && (
               <Box sx={{marginTop: '16px'}}>
                  <TextField
                     select
                     label="Add language"
                     value=""
                     size="small"
                     sx={{minWidth: 200}}
                     onChange={(e) => {
                        handleAddLanguage(e.target.value);
                        setActiveTab(localizedMetaFields.length + 1);
                     }}
                  >
                     {availableLanguages.map(lang => {
                        const langInfo = Languages.get(lang);
                        return (
                           <MenuItem key={lang} value={lang}>
                              {langInfo?.name || lang}
                           </MenuItem>
                        );
                     })}
                  </TextField>
               </Box>
            )}
         </DialogContent>
         <DialogActions>
            <Button onClick={onClose}>Close</Button>
         </DialogActions>
      </Dialog>
   );
};

const emptyMetaField = (): MasterEditMetaField => ({
   key: '',
   namespace: '',
   type: EnumMetaFieldType.Text,
   value: '',
});

type LocalizedMetaFieldGroupProps = {
   groupIndex: number
   control: Control<MasterEditorData & { newTag: string, newCol: string }>
   onRemoveGroup: () => void
}

const LocalizedMetaFieldGroup = ({groupIndex, control, onRemoveGroup}: LocalizedMetaFieldGroupProps) => {
   const {fields, append, remove} = useFieldArray({
      control,
      name: `localizedMetaFields.${groupIndex}.metaFields`
   });

   return (
      <Box>
         <MetaFieldList
            fields={fields}
            baseName={`localizedMetaFields.${groupIndex}.metaFields`}
            onAdd={() => append(emptyMetaField())}
            onRemove={remove}
         />
         <Box sx={{marginTop: '8px'}}>
            <Button
               size="small"
               color="error"
               onClick={onRemoveGroup}
            >
               Remove language
            </Button>
         </Box>
      </Box>
   );
};

type MetaFieldListProps = {
   fields: Record<string, any>[]
   baseName: string
   onAdd: () => void
   onRemove: (index: number) => void
}

const MetaFieldList = ({fields, baseName, onAdd, onRemove}: MetaFieldListProps) => {
   return (
      <Box>
         {fields.map((field, i) => (
            <Box key={field.id} sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
               <Box sx={{flex: 1}}>
                  <FormInputText name={`${baseName}.${i}.namespace`} label="Namespace"/>
               </Box>
               <Box sx={{flex: 1}}>
                  <FormInputText name={`${baseName}.${i}.key`} label="Key"/>
               </Box>
               <Box sx={{flex: 1}}>
                  <FormInputSelect
                     name={`${baseName}.${i}.type`}
                     label="Type"
                     options={metaFieldTypeOptions}
                     required
                  />
               </Box>
               <Box sx={{flex: 2}}>
                  <FormInputText name={`${baseName}.${i}.value`} label="Value"/>
               </Box>
               <IconButton size="small" onClick={() => onRemove(i)}>
                  <DeleteIcon/>
               </IconButton>
            </Box>
         ))}
         <Button
            size="small"
            startIcon={<AddIcon/>}
            onClick={onAdd}
         >
            Add meta field
         </Button>
      </Box>
   );
};
